import { Roles, Solutions } from '@app/src/wf-constants'
import { includes, isEmpty, isString, toLower } from 'lodash'

const isNonEmptyString = (value: unknown): value is string => isString(value) && !isEmpty(value)

/**
 * Check if a role is an admin role (any type of admin)
 *
 * @param role - The user's role (case-insensitive)
 * @returns true if the role is any type of admin
 */
export const isAdminRole = (role: string | undefined | null): boolean => {
  if (!isNonEmptyString(role)) return false
  return includes(toLower(role), 'admin')
}

/**
 * Check if a role is specifically the Worldfavor Employee Admin role
 *
 * @param role - The user's role (case-insensitive)
 * @returns true if the role is Worldfavor Employee Admin
 */
export const isWorldfavorEmployeeAdmin = (role: string | undefined | null): boolean => {
  if (!isNonEmptyString(role)) return false
  return toLower(role) === Roles.WfEmployeeAdmin
}

/**
 * Check if a solution supports owner restriction
 *
 * @param solution - The solution name (case-insensitive)
 * @returns true if the solution supports owner restriction
 */
export const solutionSupportsOwnerRestriction = (solution: string | undefined | null): boolean => {
  if (!isNonEmptyString(solution)) return false
  return toLower(solution) !== toLower(Solutions.Transparency)
}

/**
 * Determine if a role supports owner restriction
 *
 * @param role - The user's role (case-insensitive)
 * @returns true if the role supports owner restriction
 */
export const roleSupportsOwnerRestriction = (role: string | undefined | null): boolean => {
  return !isAdminRole(role)
}

/**
 * Normalize owner restriction value to ensure backend compatibility
 *
 * @param role - The user's role
 * @param solution - The solution name
 * @param isOwnerRestricted - The desired owner restriction value
 * @returns Normalized owner restriction value
 */
export const normalizeOwnerRestriction = (
  role: string | undefined | null,
  solution: string | undefined | null,
  isOwnerRestricted: boolean,
): boolean => {
  if (!roleSupportsOwnerRestriction(role)) {
    return false
  }

  if (!solutionSupportsOwnerRestriction(solution)) {
    return false
  }

  return isOwnerRestricted
}
